// ============================================================
// CARE JOURNEY DATA — Question Engine & Concern Definitions
// ARCHITECTURE: This layer is intentionally separate from UI.
// Future: Replace question logic with LLM backend responses.
// ============================================================

export interface CareConcern {
  id: string;
  label: string;
  icon: string;
  departmentId: string;
  emergencyRisk?: boolean;
}

export interface CareQuestion {
  id: string;
  step: number;
  question: string;
  subtitle?: string;
  options: CareOption[];
}

export interface CareOption {
  id: string;
  label: string;
  value: string;
  emergencyFlag?: boolean;
}

export interface JourneyAnswers {
  primaryNeed?: string;
  concern?: string;
  duration?: string;
  severity?: string;
  priorConsultation?: string;
}

// Primary need options shown on Screen 02
export interface PrimaryNeed {
  id: string;
  icon: string;
  label: string;
  sublabel: string;
  flowType: "health-concern" | "find-doctor" | "health-checkup" | "follow-up" | "specific-department";
}

export const primaryNeeds: PrimaryNeed[] = [
  {
    id: "health-concern",
    icon: "🩺",
    label: "I HAVE A HEALTH CONCERN",
    sublabel: "Guide me to the right care",
    flowType: "health-concern",
  },
  {
    id: "find-doctor",
    icon: "👨‍⚕️",
    label: "I NEED A DOCTOR",
    sublabel: "Find the right specialist",
    flowType: "find-doctor",
  },
  {
    id: "health-checkup",
    icon: "🔬",
    label: "I WANT A HEALTH CHECK-UP",
    sublabel: "Find the right wellness package",
    flowType: "health-checkup",
  },
  {
    id: "follow-up",
    icon: "📋",
    label: "I'M HERE FOR A FOLLOW-UP",
    sublabel: "Continue your care journey",
    flowType: "follow-up",
  },
  {
    id: "specific-department",
    icon: "🏥",
    label: "I'M LOOKING FOR A SPECIFIC DEPARTMENT",
    sublabel: "Browse our specialties",
    flowType: "specific-department",
  },
];

export const careConcerns: CareConcern[] = [
  {
    id: "heart-chest",
    label: "Heart / Chest",
    icon: "❤️",
    departmentId: "cardiology",
    emergencyRisk: true,
  },
  {
    id: "breathing",
    label: "Breathing",
    icon: "🫁",
    departmentId: "pulmonology",
    emergencyRisk: true,
  },
  {
    id: "bones-joints",
    label: "Bones & Joints",
    icon: "🦴",
    departmentId: "orthopaedics",
  },
  {
    id: "brain-nervous",
    label: "Brain & Nervous System",
    icon: "🧠",
    departmentId: "neuro-spine",
    emergencyRisk: true,
  },
  {
    id: "womens-health",
    label: "Women's Health",
    icon: "👩",
    departmentId: "gynaecology",
  },
  {
    id: "child-health",
    label: "Child Health",
    icon: "👶",
    departmentId: "paediatrics",
  },
  {
    id: "diabetes",
    label: "Diabetes / Metabolic Health",
    icon: "🩺",
    departmentId: "general-medicine",
  },
  {
    id: "cancer",
    label: "Cancer Concern",
    icon: "🎗",
    departmentId: "oncology",
  },
  {
    id: "fever-infection",
    label: "Fever / Infection",
    icon: "🌡",
    departmentId: "general-medicine",
  },
  {
    id: "other",
    label: "General / Other",
    icon: "🩻",
    departmentId: "general-medicine",
  },
];

// Questions asked after concern selection — 3 questions (steps 3, 4, 5)
export const careQuestions: CareQuestion[] = [
  {
    id: "q-duration",
    step: 3,
    question: "HOW LONG HAVE YOU BEEN EXPERIENCING THIS?",
    subtitle: "This helps us understand the nature of your concern.",
    options: [
      { id: "opt-today", label: "Today", value: "today" },
      { id: "opt-few-days", label: "A few days", value: "few-days" },
      { id: "opt-few-weeks", label: "A few weeks", value: "few-weeks" },
      { id: "opt-few-months", label: "A few months", value: "few-months" },
      { id: "opt-longer", label: "Longer", value: "longer" },
    ],
  },
  {
    id: "q-severity",
    step: 4,
    question: "HOW WOULD YOU DESCRIBE IT RIGHT NOW?",
    subtitle: "Your honest answer helps us guide you appropriately.",
    options: [
      { id: "opt-mild", label: "Mild", value: "mild" },
      { id: "opt-moderate", label: "Moderate", value: "moderate" },
      { id: "opt-severe", label: "Severe", value: "severe", emergencyFlag: true },
      { id: "opt-not-sure", label: "Not sure", value: "not-sure" },
    ],
  },
  {
    id: "q-prior",
    step: 5,
    question: "HAVE YOU CONSULTED A DOCTOR ABOUT THIS BEFORE?",
    subtitle: "This helps us understand your care history.",
    options: [
      { id: "opt-yes-prior", label: "Yes", value: "yes" },
      { id: "opt-no-prior", label: "No", value: "no" },
      { id: "opt-not-sure-prior", label: "Not sure", value: "not-sure" },
    ],
  },
];

// Journey has: Start(1) + PrimaryNeed(2) + Concern(2b, same step) + Duration(3) + Severity(4) + Prior(5)
// For progress display: we show steps 1–5 where step 1 = start, 2 = concern, 3-5 = questions
export const TOTAL_STEPS = 5;

// ============================================================
// EMERGENCY DETECTION — Rules-based, no LLM
// ============================================================
export const shouldShowEmergencyOverlay = (answers: JourneyAnswers): boolean => {
  const concern = careConcerns.find((c) => c.id === answers.concern);
  if (!concern?.emergencyRisk) return false;
  // Severe + today = potential emergency
  if (answers.severity === "severe" && answers.duration === "today") return true;
  return false;
};